import * as React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Tag, User, X} from "lucide-react";
import { graphql, useLazyLoadQuery } from 'react-relay';
import {BlogPostPageQuery} from "./__generated__/BlogPostPageQuery.graphql";
import {BRAND} from '../brand';



interface Props {
    pageId?: string;
}

const QUERY = graphql`
query BlogPostPageQuery($postId: ID!) {
    blogById(id: $postId) {
        title
        author {
            name
        }
        image
        title
        tags
        date
        content
    }
}`

const BlogPostPage: React.FC<Props> = ({pageId}) => {
    const { id } = useParams();
    pageId = pageId || id;
    const selectedPost = useLazyLoadQuery<BlogPostPageQuery>(QUERY, {postId: pageId}).blogById;
    const navigate = useNavigate();
    const navigateBlogs = () => navigate('/blogs');

    return (

        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-lg" onClick={navigateBlogs}></div>

            <div
                className="relative w-full max-w-4xl h-[90vh] bg-[#050508] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                {/* Header Image */}
                <div className="relative h-64 md:h-80 shrink-0">
                    <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-full object-cover"/>
                    <div
                        className="absolute inset-0 bg-gradient-to-t from-[#050508] via-transparent to-transparent"></div>
                    <button
                        onClick={navigateBlogs}
                        className="absolute top-6 right-6 p-2 bg-black/50 hover:bg-white hover:text-black text-white rounded-full backdrop-blur-md transition-all border border-white/10"
                    >
                        <X className="w-6 h-6"/>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 md:p-12">
                    <div className="max-w-3xl mx-auto">
                        <div className="flex flex-wrap gap-2 mb-6">
                            {selectedPost.tags.map(tag => (
                                <span key={tag}
                                      className="flex items-center gap-1 px-3 py-1 bg-gray-900 text-gray-300 text-xs font-bold uppercase rounded border border-gray-800">
                      <Tag className="w-3 h-3"/> {tag}
                    </span>
                            ))}
                        </div>

                        <h1 className="text-3xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
                            {selectedPost.title}
                        </h1>

                        <div className="flex items-center gap-6 border-b border-gray-800 pb-8 mb-8">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                                    <User className="w-5 h-5 text-gray-400"/>
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-white">{selectedPost.author.name}</div>
                                    <div className="text-xs text-gray-500">{BRAND.name} Specialist</div>
                                </div>
                            </div>
                            <div className="h-8 w-px bg-gray-800"></div>
                            <div className="text-sm text-gray-500">
                                {new Date(selectedPost.date).toLocaleDateString()}
                            </div>
                        </div>

                        <div
                            className="prose prose-invert prose-lg max-w-none"
                            dangerouslySetInnerHTML={{__html: selectedPost.content}}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-800 bg-[#0a0a0f] flex justify-between items-center">
                    <span className="text-xs text-gray-500">Share this article</span>
                    <button
                        onClick={navigateBlogs}
                        className="px-6 py-2 border border-gray-700 hover:border-white text-white text-sm font-bold uppercase rounded transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export default BlogPostPage;